// NotificationSheet — mobile bottom-sheet на vaul BottomSheet (грабер сверху +
// нативный свайп-вниз для закрытия + скруглённые углы). Канон всех мобильных
// шторок. Spec: docs/ui-spec/notifications.sheet.mobile.md
//
// Тактильный редизайн «Картотека» (§8.V): контент — date-grouped тактильный
// список. Крестик-X не нужен (закрытие свайпом/тапом по подложке).

import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetDescription,
	BottomSheetTitle,
} from "@studyhub/ui/components/bottom-sheet";
import { Button } from "@studyhub/ui/components/button";
import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { IconBell, IconChecks, IconSettings } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { NotificationItemData } from "./notification-item";
import { NotificationList } from "./notification-list";
import { useMarkAllNotificationsRead } from "./use-mark-all-read";

interface NotificationSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	items: NotificationItemData[];
	isLoading?: boolean;
	isError?: boolean;
}

export function NotificationSheet({
	open,
	onOpenChange,
	items,
	isLoading,
	isError,
}: NotificationSheetProps) {
	const { t } = useTranslation("notifications");
	// Счётчик считаем по реальным данным, не из UI-store.
	const unreadCount = useMemo(() => items.filter((i) => !i.read).length, [items]);
	const markAllRead = useMarkAllNotificationsRead();

	return (
		<BottomSheet open={open} onOpenChange={onOpenChange}>
			<BottomSheetContent className="flex h-[88dvh] flex-col gap-0 overflow-hidden p-0">
				<div className="flex shrink-0 flex-row items-center gap-2.5 border-b border-border px-4 py-3 text-left">
					<CategoryTile category="cards" icon={<IconBell stroke={1.75} />} size="sm" />
					<div className="min-w-0 flex-1">
						<BottomSheetTitle className="text-base">{t("title")}</BottomSheetTitle>
						<BottomSheetDescription className="tabular-nums">
							{t("unreadCount", { count: unreadCount })}
						</BottomSheetDescription>
					</div>
					{/* «Прочитать все» — синяя иконка-кнопка, только при непрочитанных. */}
					{unreadCount > 0 && (
						<IconTooltip label={t("markAllRead")}>
							<Button
								variant="outline"
								size="icon"
								className="shrink-0 cursor-pointer"
								onClick={() => void markAllRead()}
								aria-label={t("markAllRead")}
							>
								<IconChecks size={18} className="text-primary" />
							</Button>
						</IconTooltip>
					)}
				</div>
				<div className="min-h-0 flex-1 overflow-hidden">
					<NotificationList
						items={items}
						isLoading={isLoading}
						isError={isError}
						onItemSelect={() => onOpenChange(false)}
					/>
				</div>
				<footer className="shrink-0 border-t border-border px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
					<Button
						asChild
						variant="ghost"
						size="sm"
						className="min-h-[44px] w-full cursor-pointer justify-start"
					>
						<Link to="/settings/notifications">
							<IconSettings size={14} />
							{t("settings")}
						</Link>
					</Button>
				</footer>
			</BottomSheetContent>
		</BottomSheet>
	);
}
